/**
 * Shared instant parser and formatter for on-screen date-times.
 * Offset-less datetimes are UTC. Date-only strings are not instants.
 * Omit `timeZone` to use the saved profile zone, or the device zone when none is saved.
 * Do not set `timeZoneName` on screen.
 */

import { getDisplayTimeZone } from '../services/displayTimeZone';

const isDigits = (value: string): boolean => {
  if (value.length === 0) {
    return false;
  }
  for (const ch of value) {
    if (ch < '0' || ch > '9') {
      return false;
    }
  }
  return true;
};

const isDatePart = (value: string): boolean =>
  value.length === 10 &&
  value[4] === '-' &&
  value[7] === '-' &&
  isDigits(value.slice(0, 4)) &&
  isDigits(value.slice(5, 7)) &&
  isDigits(value.slice(8, 10));

const isNumericOffset = (zone: string): boolean =>
  zone.length === 6 &&
  (zone[0] === '+' || zone[0] === '-') &&
  zone[3] === ':' &&
  isDigits(zone.slice(1, 3)) &&
  isDigits(zone.slice(4, 6));

const parsedDate = (source: string): Date | null => {
  const date = new Date(source);
  return Number.isNaN(date.getTime()) ? null : date;
};

/** `YYYY-MM-DDTHH:mm:ss[.fff][Z|±hh:mm]`, space allowed instead of `T`. */
const readDateTime = (value: string): string | null => {
  const separatorAt =
    value.indexOf('T') >= 0 ? value.indexOf('T') : value.indexOf(' ');
  if (separatorAt !== 10) {
    return null;
  }
  const datePart = value.slice(0, 10);
  if (!isDatePart(datePart)) {
    return null;
  }
  const rest = value.slice(11);
  if (
    rest.length < 8 ||
    rest[2] !== ':' ||
    rest[5] !== ':' ||
    !isDigits(rest.slice(0, 2)) ||
    !isDigits(rest.slice(3, 5)) ||
    !isDigits(rest.slice(6, 8))
  ) {
    return null;
  }
  let index = 8;
  if (rest[index] === '.') {
    const fractionStart = index + 1;
    index = fractionStart;
    while (index < rest.length && rest[index] >= '0' && rest[index] <= '9') {
      index += 1;
    }
    const fractionLength = index - fractionStart;
    if (fractionLength < 1 || fractionLength > 9) {
      return null;
    }
  }
  const zone = rest.slice(index);
  if (zone !== '' && zone !== 'Z' && zone !== 'z' && !isNumericOffset(zone)) {
    return null;
  }
  return `${datePart}T${rest.slice(0, index)}${zone === '' ? 'Z' : zone}`;
};

const parseInstant = (value: unknown): Date | null => {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  if (typeof value !== 'string') {
    return null;
  }
  const trimmed = value.trim();
  if (trimmed === '' || isDatePart(trimmed)) {
    return null;
  }
  const iso = readDateTime(trimmed);
  return iso ? parsedDate(iso) : null;
};

const format = (
  value: unknown,
  options?: Intl.DateTimeFormatOptions & { locale?: string },
): string | null => {
  const date = parseInstant(value);
  if (!date) {
    return null;
  }
  const { locale, timeZone, ...intlOptions } = options ?? {};
  const zone = timeZone ?? getDisplayTimeZone();
  return new Intl.DateTimeFormat(locale, {
    ...intlOptions,
    ...(zone ? { timeZone: zone } : {}),
  }).format(date);
};

/** Calendar day of an instant in the zone `format` uses when `timeZone` is omitted. */
const calendarDay = (
  value: Date,
): { year: number; month: number; day: number } => {
  const zone = getDisplayTimeZone();
  const parts = new Intl.DateTimeFormat('en-US', {
    ...(zone ? { timeZone: zone } : {}),
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(value);
  const read = (type: Intl.DateTimeFormatPartTypes): number =>
    Number(parts.find(part => part.type === type)?.value ?? '');
  return { year: read('year'), month: read('month'), day: read('day') };
};

export const dateFormatting = {
  parseInstant,
  format,
  calendarDay,
};
